import React, { useState, useEffect, type CSSProperties } from 'react';
import type {
  AuthorRequest,
  AuthorCoauthorAd,
  AuthorReadyManuscript,
} from '../types';
import { authorApi } from '@/features/workspace/api';
import { useToast } from '@/hooks/useToast';
import {
  CloseIcon,
  PlusIcon,
  FileDocumentIcon,
  EyeIcon,
} from './AuthorIcons';

interface Props {
  onClose: () => void;
  onRequestWizard: () => void;
}

const STATUS_META: Record<string, { label: string; color: string }> = {
  NEW: { label: 'Новая', color: '#66b7e8' },
  IN_REVIEW: { label: 'На экспертизе', color: '#e2ad3a' },
  ACCEPTED_BY_COORDINATOR: { label: 'Принята координатором', color: '#49ddb7' },
  CONTRACT_SIGNED: { label: 'Договор подписан', color: '#49ddb7' },
  IN_PROGRESS: { label: 'В работе', color: '#9e80ff' },
  COMPLETED: { label: 'Завершена', color: '#49ddb7' },
  ACTIVE: { label: 'Активно', color: '#49ddb7' },
};

export const MyRequestsOverlay: React.FC<Props> = ({ onClose, onRequestWizard }) => {
  const { notify } = useToast();
  const [activeTab, setActiveTab] = useState<'ADS' | 'RESPONSES' | 'MANUSCRIPTS' | 'SERVICES'>('SERVICES');
  const [requests, setRequests] = useState<AuthorRequest[]>([]);
  const [ads, setAds] = useState<AuthorCoauthorAd[]>([]);
  const [manuscripts, setManuscripts] = useState<AuthorReadyManuscript[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<AuthorRequest | null>(null);
  const [showCreateAdModal, setShowCreateAdModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const [newAdTitle, setNewAdTitle] = useState('');
  const [newAdSubject, setNewAdSubject] = useState('Педагогические науки');
  const [newAdDb, setNewAdDb] = useState('Scopus (Q1-Q2)');
  const [newAdVacant, setNewAdVacant] = useState(2);
  const [newAdCompetencies, setNewAdCompetencies] = useState('Анализ данных, эконометрика');
  const [newAdDesc, setNewAdDesc] = useState('');

  const loadData = async () => {
    try {
      setLoading(true);
      const [reqs, adList, mss] = await Promise.all([
        authorApi.getRequests(),
        authorApi.getAds(),
        authorApi.getReadyManuscripts(),
      ]);
      setRequests(reqs);
      setAds(adList);
      setManuscripts(mss.filter((m) => m.user_has_interest));
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateAdSubmit = async () => {
    if (!newAdTitle.trim()) {
      notify('Укажите название статьи для объявления', 'error');
      return;
    }
    try {
      await authorApi.createAd({
        title: newAdTitle,
        subject_area: newAdSubject,
        target_database: newAdDb,
        vacant_places_count: newAdVacant,
        required_competencies: newAdCompetencies.split(',').map((s) => s.trim()),
        description: newAdDesc,
      });
      setShowCreateAdModal(false);
      setNewAdTitle('');
      setNewAdDesc('');
      loadData();
    } catch (err: any) {
      notify(err?.message || 'Ошибка при создании объявления', 'error');
    }
  };

  const statusLabel = (status: string) => STATUS_META[status]?.label || status;
  const statusColor = (status: string) => STATUS_META[status]?.color || '#8faebb';

  const getStatusBadge = (status: string) => (
    <span className="cabinet-badge" style={{ '--badge': statusColor(status) } as CSSProperties}>
      {statusLabel(status)}
    </span>
  );

  return (
    <div className="cab-overlay">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Заявки автора</small>
              <h2>Реестр авторских заявок и объявлений</h2>
            </div>
          </div>
          <div className="cab-top-actions">
            <button type="button" className="cab-btn primary" onClick={onRequestWizard}>
              + Создать заявку
            </button>
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="cab-tabs">
          {[
            { id: 'SERVICES', label: 'Научные заявки', count: requests.length },
            { id: 'ADS', label: 'Поиск соавторов', count: ads.length },
            { id: 'RESPONSES', label: 'Отклики', count: ads.reduce((acc, a) => acc + (a.responses_count || 0), 0) },
            { id: 'MANUSCRIPTS', label: 'Готовые статьи', count: manuscripts.length },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`cab-tab${activeTab === tab.id ? ' is-active' : ''}`}
            >
              {tab.label} · {tab.count}
            </button>
          ))}
        </div>

        <div className="cab-body">
          {loading ? (
            <div className="cabinet-note">Загрузка обращений…</div>
          ) : activeTab === 'SERVICES' ? (
            requests.length === 0 ? (
              <div className="cabinet-note">
                <FileDocumentIcon className="w-12 h-12" style={{ marginBottom: 8, opacity: 0.5 }} />
                <strong style={{ display: 'block', marginBottom: 6 }}>Пока нет поданных заявок</strong>
                <p style={{ margin: '0 0 12px' }}>
                  Воспользуйтесь мастером создания заявки, чтобы подать рукопись на сопровождение, подбор журнала или перевод.
                </p>
                <button type="button" className="cabinet-btn primary" onClick={onRequestWizard}>
                  Подать первую заявку
                </button>
              </div>
            ) : (
              <div className="cabinet-stack">
                {requests.map((req) => (
                  <article
                    key={req.id}
                    className="cabinet-card"
                    style={{ ['--accent' as string]: '#41d2ff', cursor: 'pointer' }}
                    onClick={() => setSelectedRequest(req)}
                  >
                    <div className="cabinet-actions" style={{ marginBottom: 8, alignItems: 'center' }}>
                      <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                        {req.request_number}
                      </span>
                      {getStatusBadge(req.status)}
                      <span style={{ fontSize: 12, color: '#8faebb' }}>{req.database_index}</span>
                      <span className="cabinet-spacer" />
                      {req.cost_estimate ? (
                        <strong style={{ fontSize: 13 }}>{req.cost_estimate.toLocaleString()} ₸</strong>
                      ) : null}
                      <button
                        type="button"
                        className="cabinet-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRequest(req);
                        }}
                        aria-label="Просмотр"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <h3>{req.title}</h3>
                    <p>
                      {req.subject_area} · Соавторов: {req.authors?.length || 1} · Ответственный:{' '}
                      {req.current_responsible || 'Координатор публикаций'}
                    </p>
                  </article>
                ))}
              </div>
            )
          ) : activeTab === 'ADS' ? (
            <div className="cabinet-stack">
              <div className="cabinet-actions" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ margin: 0, color: '#8faebb', fontSize: 13 }}>
                  Объявления о поиске соавторов в ваши научные проекты.
                </p>
                <button type="button" className="cabinet-btn primary" onClick={() => setShowCreateAdModal(true)}>
                  <PlusIcon className="w-4 h-4" />
                  Подать объявление
                </button>
              </div>

              {ads.map((ad) => (
                <article key={ad.id} className="cabinet-card" style={{ ['--accent' as string]: '#49ddb7' }}>
                  <div className="cabinet-actions" style={{ marginBottom: 8, alignItems: 'center' }}>
                    <span className="cabinet-badge" style={{ '--badge': '#49ddb7' } as CSSProperties}>
                      {ad.target_database}
                    </span>
                    <span style={{ fontSize: 12, color: '#8faebb' }}>{ad.subject_area}</span>
                    <span className="cabinet-spacer" />
                    <span className="cabinet-badge" style={{ '--badge': '#49ddb7' } as CSSProperties}>
                      Свободных мест: {ad.vacant_places_count}
                    </span>
                  </div>
                  <h3>{ad.title}</h3>
                  <p>{ad.description}</p>
                  <div className="cabinet-actions" style={{ marginTop: 10 }}>
                    {ad.required_competencies?.map((c, i) => (
                      <span key={i} className="cabinet-badge" style={{ '--badge': '#8faebb' } as CSSProperties}>
                        {c}
                      </span>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          ) : activeTab === 'RESPONSES' ? (
            <div className="cabinet-stack">
              {ads.filter((a) => (a.responses_count || 0) > 0).length === 0 ? (
                <div className="cabinet-note">Пока нет новых откликов на ваши объявления.</div>
              ) : (
                ads.map((ad) => (
                  <article key={ad.id} className="cabinet-card" style={{ ['--accent' as string]: '#49ddb7' }}>
                    <h3>{ad.title}</h3>
                    <p style={{ color: '#49ddb7' }}>Получено откликов: {ad.responses_count || 1}</p>
                    <div className="cabinet-note" style={{ marginTop: 10 }}>
                      <strong>Кандидат: Докторант PhD (КазНУ им. аль-Фараби)</strong>
                      <p style={{ margin: '6px 0 0' }}>
                        Предлагаемый вклад: Эконометрический анализ данных, подготовка раздела Discussion
                      </p>
                      <div className="cabinet-actions" style={{ marginTop: 10 }}>
                        <button type="button" className="cabinet-btn primary">
                          Принять в команду
                        </button>
                        <button type="button" className="cabinet-btn">
                          Отклонить
                        </button>
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>
          ) : (
            <div className="cabinet-stack">
              {manuscripts.length === 0 ? (
                <div className="cabinet-note">
                  У вас нет активных заявок на готовые статьи. Вы можете выбрать рукопись в разделе «Опросы и формы».
                </div>
              ) : (
                manuscripts.map((m) => (
                  <article key={m.id} className="cabinet-card" style={{ ['--accent' as string]: '#41d2ff' }}>
                    <div className="cabinet-actions" style={{ marginBottom: 8, alignItems: 'center' }}>
                      <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                        {m.code}
                      </span>
                      <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                        {m.target_database} {m.quartile && `(${m.quartile})`}
                      </span>
                    </div>
                    <h3>{m.title}</h3>
                    <p>{m.abstract_snippet}</p>
                    <p style={{ color: '#e2ad3a', fontWeight: 900 }}>
                      Свободных соавторских мест: {m.available_coauthor_slots}
                    </p>
                  </article>
                ))
              )}
            </div>
          )}
        </div>

        <div className="cabinet-actions" style={{ padding: '12px 18px', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,.08)' }}>
          <span style={{ fontSize: 12, color: '#8faebb' }}>Синхронизировано с базой данных</span>
          <button type="button" className="cabinet-btn" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>

      {selectedRequest && (
        <div className="cabinet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cabinet-modal cabinet-modal--passport">
            <div className="cabinet-modal-head">
              <div>
                <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                  {selectedRequest.request_number}
                </span>
                <h2>Неизменяемый снимок поданной заявки</h2>
              </div>
              <button type="button" className="cabinet-btn" onClick={() => setSelectedRequest(null)} aria-label="Закрыть">
                <CloseIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="cabinet-modal-body">
              <div className="cabinet-stack">
                <article className="cabinet-card">
                  <div style={{ color: '#8faebb', fontSize: 12 }}>Тема исследования</div>
                  <h3 style={{ marginTop: 6 }}>{selectedRequest.title}</h3>
                  {selectedRequest.description ? <p>{selectedRequest.description}</p> : null}
                </article>

                <div className="cabinet-grid2">
                  <article className="cabinet-card">
                    <div style={{ color: '#8faebb', fontSize: 12 }}>Вариант работы</div>
                    <strong style={{ display: 'block', marginTop: 6 }}>{selectedRequest.work_option}</strong>
                  </article>
                  <article className="cabinet-card">
                    <div style={{ color: '#8faebb', fontSize: 12 }}>Целевая база</div>
                    <strong style={{ display: 'block', marginTop: 6 }}>{selectedRequest.database_index}</strong>
                  </article>
                </div>

                <section className="cabinet-panel">
                  <header>
                    <div>
                      <h3>Авторский коллектив</h3>
                    </div>
                  </header>
                  <div className="cabinet-panel-body" style={{ display: 'grid', gap: 8 }}>
                    {selectedRequest.authors?.map((a, i) => (
                      <div key={i} className="cabinet-file-row">
                        <div>
                          <strong>
                            {a.full_name} {a.is_corresponding ? '(Корр.)' : ''}
                          </strong>
                          <small>
                            {a.affiliation || 'ВУЗ'} · {a.orcid_id || 'Без ORCID'}
                          </small>
                        </div>
                        <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                          {a.share_percent || 0}%
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                {selectedRequest.history && selectedRequest.history.length > 0 ? (
                  <section className="cabinet-panel">
                    <header>
                      <div>
                        <h3>История движения статуса</h3>
                      </div>
                    </header>
                    <div className="cabinet-panel-body">
                      <div className="cabinet-timeline">
                        {selectedRequest.history.map((h) => (
                          <div key={h.id} className="cabinet-timeline-item">
                            <div className="cabinet-actions" style={{ justifyContent: 'space-between' }}>
                              <div>
                                <span className="cabinet-badge" style={{ '--badge': statusColor(h.status) } as CSSProperties}>
                                  {statusLabel(h.status)}
                                </span>
                                {h.note ? <span style={{ marginLeft: 8, color: '#8faebb' }}>— {h.note}</span> : null}
                              </div>
                              <span style={{ fontSize: 11, color: '#7f9eac' }}>
                                {new Date(h.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>
                ) : null}
              </div>

              <div className="cabinet-actions" style={{ marginTop: 16, justifyContent: 'flex-end' }}>
                <button type="button" className="cabinet-btn primary" onClick={() => setSelectedRequest(null)}>
                  Закрыть просмотр
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreateAdModal && (
        <div className="cabinet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cabinet-modal">
            <div className="cabinet-modal-head">
              <div>
                <h2>Новое объявление о поиске соавторов</h2>
              </div>
              <button type="button" className="cabinet-btn" onClick={() => setShowCreateAdModal(false)} aria-label="Закрыть">
                <CloseIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="cabinet-modal-body">
              <div className="cabinet-stack">
                <label className="cabinet-field">
                  <span>Название темы / статьи *</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={newAdTitle}
                    onChange={(e) => setNewAdTitle(e.target.value)}
                    placeholder="Тематика исследования..."
                  />
                </label>
                <div className="cabinet-grid2">
                  <label className="cabinet-field">
                    <span>Научная область</span>
                    <input
                      type="text"
                      className="cabinet-input"
                      value={newAdSubject}
                      onChange={(e) => setNewAdSubject(e.target.value)}
                    />
                  </label>
                  <label className="cabinet-field">
                    <span>Целевая база</span>
                    <input
                      type="text"
                      className="cabinet-input"
                      value={newAdDb}
                      onChange={(e) => setNewAdDb(e.target.value)}
                    />
                  </label>
                </div>
                <div className="cabinet-grid2">
                  <label className="cabinet-field">
                    <span>Вакантных мест соавторов</span>
                    <input
                      type="number"
                      className="cabinet-input"
                      value={newAdVacant}
                      onChange={(e) => setNewAdVacant(Number(e.target.value))}
                    />
                  </label>
                  <label className="cabinet-field">
                    <span>Требуемые компетенции (через запятую)</span>
                    <input
                      type="text"
                      className="cabinet-input"
                      value={newAdCompetencies}
                      onChange={(e) => setNewAdCompetencies(e.target.value)}
                    />
                  </label>
                </div>
                <label className="cabinet-field">
                  <span>Краткое описание проекта</span>
                  <textarea
                    className="cabinet-textarea"
                    value={newAdDesc}
                    onChange={(e) => setNewAdDesc(e.target.value)}
                    rows={3}
                  />
                </label>
              </div>
              <div className="cabinet-actions" style={{ marginTop: 16, justifyContent: 'flex-end' }}>
                <button type="button" className="cabinet-btn" onClick={() => setShowCreateAdModal(false)}>
                  Отмена
                </button>
                <button type="button" className="cabinet-btn primary" onClick={handleCreateAdSubmit}>
                  Опубликовать объявление
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
